import { useEffect, useMemo } from "react";
import { WordCloud } from "../../../components/word-cloud";
import { Word } from "../../../components/word-cloud/utils";

export interface WordCloudWrapperProps {
  item: {
    values: string;
    [key: string]: string;
  };
  rawValue?: string;
  onChange: (e: { target: { value: number | null; rawValue: string } }) => void;
}
export function WordCloudWrapper({
  item,
  onChange,
  rawValue,
}: WordCloudWrapperProps) {
  const itemValues = item.values || "0:0"; // Fallback to "0:0" if values are not provided
  const [wordsRequiredStr, totalWordsStr] = itemValues.split(":");
  const wordsRequired = parseInt(wordsRequiredStr, 10);
  const totalWord = parseInt(totalWordsStr, 10);

  const words = useMemo(() => {
    return [...Array(totalWord)].map<Word>((_, i) => {
      const strValue = item[`v${i + 1}`];
      const [label, valueStr] = strValue.split(":");
      return {
        value: parseFloat(valueStr),
        label,
      };
    });
  }, [totalWord, item]);

  const initialValue = useMemo(() => {
    if (rawValue) {
      const parsedValue = JSON.parse(rawValue);
      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((word, i) => {
            return words.find(w => w.label === word.label);
          })
          .filter(Boolean) as Word[];
      }
    }
    return [];
  }, [rawValue, words]);

  useEffect(() => {
    if (initialValue.length && words.length) {
      const selectedWords = words.filter(word =>
        initialValue.find(
          (initialValueWord: Word) => word.label === initialValueWord.label
        )
      );
      handleWordsChange(selectedWords);
    }
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  const handleWordsChange = (selectedWords: Word[]) => {
    const rawValue = JSON.stringify(selectedWords);
    if (selectedWords.length >= wordsRequired) {
      const totalValue = selectedWords.reduce(
        (sum, word) => sum + word.value,
        0
      );
      const averageValue = totalValue / selectedWords.length;
      onChange({
        target: {
          value: averageValue,
          rawValue,
        },
      });
    } else {
      onChange({
        target: {
          value: null,
          rawValue,
        },
      });
    }
  };

  return (
    <WordCloud
      words={words}
      initialWords={initialValue}
      onWordsChange={handleWordsChange}
      maxSelectedWords={wordsRequired}
    />
  );
}
